import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import type {
  ChatActionResult,
  ChatAdapter,
  ChatMessage,
  ChatSnapshot,
  ChatTab,
} from "./types";

export const MAX_CHAT_MESSAGE_LENGTH = 500;

const UNAVAILABLE_SNAPSHOT: ChatSnapshot = Object.freeze({
  status: "unavailable",
  messages: [],
  canSend: false,
  hasOlder: false,
  loadingOlder: false,
  unreadCount: 0,
  unreadMentions: 0,
  onlineCount: null,
  errorMessage: null,
  pinnedMessage: null,
  banned: false,
  rateLimitSeconds: 0,
});

type ChatContextValue = {
  snapshot: ChatSnapshot;
  isOpen: boolean;
  activeTab: ChatTab;
  replyTarget: ChatMessage | null;
  sending: boolean;
  actionError: string | null;
  open: () => void;
  close: () => void;
  toggle: () => void;
  selectTab: (tab: ChatTab) => void;
  setReplyTarget: (message: ChatMessage | null) => void;
  sendMessage: (text: string) => Promise<boolean>;
  loadOlder: () => Promise<ChatActionResult>;
  react: (messageId: string, emoji: string) => Promise<ChatActionResult>;
  /** Clear unread state for surfaces (e.g. fullscreen chat) that show the
   * feed without opening the drawer. */
  markViewed: () => void;
};

const ChatContext = createContext<ChatContextValue | null>(null);
const subscribeUnavailable = () => () => undefined;
const getUnavailableSnapshot = () => UNAVAILABLE_SNAPSHOT;

export function ChatProvider({
  adapter = null,
  children,
}: {
  adapter?: ChatAdapter | null;
  children: ReactNode;
}) {
  const subscribe = useCallback(
    (listener: () => void) => adapter?.subscribe(listener) ?? (() => undefined),
    [adapter],
  );
  const readSnapshot = useCallback(
    () => adapter?.getSnapshot() ?? UNAVAILABLE_SNAPSHOT,
    [adapter],
  );
  const snapshot = useSyncExternalStore(
    adapter ? subscribe : subscribeUnavailable,
    adapter ? readSnapshot : getUnavailableSnapshot,
    getUnavailableSnapshot,
  );
  const [isOpen, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ChatTab>("chat");
  const [replyTarget, setReplyTarget] = useState<ChatMessage | null>(null);
  const [sending, setSending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    adapter?.connect?.();
    return () => adapter?.disconnect?.();
  }, [adapter]);

  useEffect(() => {
    if (isOpen && activeTab === "chat" && snapshot.unreadCount > 0) {
      adapter?.markViewed?.();
    }
  }, [activeTab, adapter, isOpen, snapshot.unreadCount]);

  const open = useCallback(() => {
    setOpen(true);
    setActionError(null);
    adapter?.markViewed?.();
  }, [adapter]);

  const close = useCallback(() => {
    setOpen(false);
    setReplyTarget(null);
  }, []);

  const selectTab = useCallback(
    (tab: ChatTab) => {
      setActiveTab(tab);
      setReplyTarget(null);
      if (tab === "chat") adapter?.markViewed?.();
    },
    [adapter],
  );

  const sendMessage = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!adapter || !text || text.length > MAX_CHAT_MESSAGE_LENGTH) {
        return false;
      }
      setSending(true);
      setActionError(null);
      try {
        const result = await adapter.sendMessage(text, {
          ...(replyTarget ? { replyToId: replyTarget.id } : {}),
        });
        if (!result.ok) {
          setActionError(result.message);
          return false;
        }
        setReplyTarget(null);
        return true;
      } finally {
        setSending(false);
      }
    },
    [adapter, replyTarget],
  );

  const loadOlder = useCallback(async () => {
    if (!adapter?.loadOlder) {
      return { ok: false, message: "Chat history is not connected." } as const;
    }
    return adapter.loadOlder();
  }, [adapter]);

  const react = useCallback(
    async (messageId: string, emoji: string) => {
      if (!adapter?.react) {
        return {
          ok: false,
          message: "Chat reactions are not connected.",
        } as const;
      }
      return adapter.react(messageId, emoji);
    },
    [adapter],
  );

  const markViewed = useCallback(() => {
    adapter?.markViewed?.();
  }, [adapter]);

  const value = useMemo<ChatContextValue>(
    () => ({
      snapshot,
      isOpen,
      activeTab,
      replyTarget,
      sending,
      actionError,
      open,
      close,
      toggle: () => (isOpen ? close() : open()),
      selectTab,
      setReplyTarget,
      sendMessage,
      loadOlder,
      react,
      markViewed,
    }),
    [
      snapshot,
      isOpen,
      activeTab,
      replyTarget,
      sending,
      actionError,
      open,
      close,
      selectTab,
      sendMessage,
      loadOlder,
      react,
      markViewed,
    ],
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const value = useContext(ChatContext);
  if (!value) throw new Error("useChat must be used inside ChatProvider");
  return value;
}
